/**
 * Bundle 76 - Public post-event survey page keyed by HMAC token.
 * Bundle 102 (Phase 0) - Refactored container. All field renderers +
 * DirectConnectionCard + MissedConnectionRequest live in
 * `components/survey/fields/QuestionField.jsx`. Validation +
 * autosave hooks in `hooks/useSurveyValidation.js` +
 * `hooks/useSurveyAutosave.js`.
 *
 * No em/en dashes, no eslint-disable, no photo/image alt copy.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { ChevronRight, ChevronLeft, Sparkles, Check, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { QuestionField } from '@/components/survey/fields/QuestionField';
import { useSurveyValidation } from '@/hooks/useSurveyValidation';
import { useSurveyAutosave } from '@/hooks/useSurveyAutosave';

const BASE = process.env.REACT_APP_BACKEND_URL;
const surveyApi = axios.create({ baseURL: `${BASE}/api` });

const SurveyTokenPage = () => {
  const { token } = useParams();
  // Bundle 78 Task 5 - preview_only query param disables all writes
  // so the in-app preview iframe never contaminates any bucket.
  const previewOnly = React.useMemo(() => {
    try {
      return new URLSearchParams(window.location.search).get('preview_only') === '1';
    } catch (_e) { return false; }
  }, []);
  const [data, setData] = useState(null);
  const [step, setStep] = useState(1);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [correctionActive, setCorrectionActive] = useState(false);
  const validationSummaryRef = useRef(null);
  const liveRef = useRef(null);

  // Bundle 102 - hoisted hooks: autosave + per-step validation.
  const {
    autosaveFailed, autosavePending, scheduleAutosave, persistAutosave,
    setAutosaveFailed, setAutosavePending,
  } = useSurveyAutosave({ surveyApi, token, previewOnly });

  const {
    validateStep, stepErrors, setStepErrors, evalConditional,
  } = useSurveyValidation({ data, answers });

  const fetchSurvey = useCallback(async () => {
    try {
      const r = await surveyApi.get(`/survey/${token}`);
      setData(r.data);
      const stored = r.data.response?.answers || {};
      setAnswers(stored);
      setCorrectionActive(!!stored.attendance_correction);
      if (r.data.response?.submitted_at) setSubmitted(true);
      const savedStep = Number(stored._current_step || r.data.response?.current_step || 1);
      const clamped = Math.max(1, Math.min(4, savedStep));
      setStep(clamped);
    } catch (e) { setError(e?.response?.data?.detail || 'Survey not found.'); }
  }, [token]);
  useEffect(() => { fetchSurvey(); }, [fetchSurvey]);

  const setAnswer = (qid, v) => {
    setAnswers((prev) => { const next = { ...prev, [qid]: v }; scheduleAutosave(next); return next; });
    setStepErrors((errs) => errs.filter((e) => e.id !== qid));
  };

  const goToStep = useCallback((n) => {
    const next = Math.max(1, Math.min(4, n));
    if (next > step) {
      const errs = validateStep(step);
      if (errs.length) {
        setStepErrors(errs);
        setTimeout(() => {
          try { validationSummaryRef.current?.focus(); } catch (_) { /* noop */ }
        }, 0);
        return;
      }
    }
    setStepErrors([]);
    setStep(next);
    setAnswers((prev) => {
      const patched = { ...prev, _current_step: next };
      scheduleAutosave(patched);
      return patched;
    });
    setTimeout(() => {
      try {
        const h = document.querySelector(`[data-testid="step-heading-${next}"]`);
        if (h) h.focus();
      } catch (_) { /* noop */ }
    }, 30);
  }, [scheduleAutosave, step, validateStep, setStepErrors]);

  const retryAutosave = async () => {
    if (autosavePending) await persistAutosave(autosavePending);
    else await persistAutosave(answers);
  };

  const toggleAttendanceCorrection = async () => {
    if (previewOnly) {
      toast.info('Preview only. Correction toggle would flip the branch here.');
      setCorrectionActive(true);
      setData((d) => (d ? { ...d, is_attendee: true } : d));
      setStep(1);
      return;
    }
    try {
      await surveyApi.post(`/survey/${token}/attendance-correction`, { note: '' });
      setCorrectionActive(true);
      await fetchSurvey();
      toast.success('We noted that you attended. Full survey now open.');
      setStep(1);
    } catch (e) { toast.error('Could not save your correction. Please try again.'); }
  };

  const submit = async () => {
    if (previewOnly) {
      toast.info('Preview only. Submit is disabled.');
      return;
    }
    const finalErrs = validateStep(step);
    if (finalErrs.length) {
      setStepErrors(finalErrs);
      setTimeout(() => {
        try { validationSummaryRef.current?.focus(); } catch (_) { /* noop */ }
      }, 0);
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      await surveyApi.post(`/survey/${token}/submit`, { answers });
      setSubmitted(true);
      if (!data?.is_attendee) {
        try { const r = await surveyApi.get(`/survey/${token}/recap`); setData((d) => ({ ...d, recap: r.data })); } catch (e) { /* no recap */ }
      }
      toast.success('Thanks. Your reflection is saved.');
    } catch (e) {
      const detail = e?.response?.data?.detail;
      // Bundle 102 - 422 with per_step_errors now takes precedence
      // over the older missing_required shape.
      if (detail && detail.per_step_errors) {
        const firstStep = detail.first_invalid_step
          || Object.keys(detail.per_step_errors).find(
              (k) => (detail.per_step_errors[k] || []).length);
        setStep(Number(firstStep) || 1);
        setError('Please complete the highlighted fields on this step.');
      } else if (detail && detail.error === 'ALLOW_INTRODUCTIONS_DISABLED') {
        setError(detail.message);
      } else if (detail && detail.missing_required) {
        setError(`Please complete: ${detail.missing_required.join(', ')}`);
      } else {
        setError(typeof detail === 'string' ? detail : 'Could not submit. Please retry.');
      }
    } finally { setSubmitting(false); }
  };

  const stepsMemo = useMemo(() => {
    if (!data) return { steps: [], max: 4 };
    const groups = {};
    for (const q of data.questions || []) {
      if (q.type === 'attendance_toggle') continue;
      if (q.hidden) continue;
      if (!evalConditional(q)) continue;
      const s = q.step || 1;
      groups[s] = groups[s] || [];
      groups[s].push(q);
    }
    const keys = [1, 2, 3, 4];
    return { steps: keys.map((k) => ({ step: k, questions: groups[k] || [] })), max: 4 };
  }, [data, evalConditional]);

  if (error && !data) return (
    <div className="min-h-screen flex items-center justify-center bg-neutral-50 p-6" data-testid="survey-error">
      <div className="max-w-md text-center">
        <div className="text-primary-900 font-semibold text-lg mb-2">This survey is not available.</div>
        <div className="text-neutral-600 text-sm">{error}</div>
      </div>
    </div>
  );
  if (!data) return <div className="min-h-screen flex items-center justify-center text-neutral-500">Loading...</div>;

  if (submitted) {
    const recap = data.recap;
    return (
      <div className="min-h-screen bg-neutral-50" data-testid="survey-thankyou">
        <div className="max-w-2xl mx-auto p-6 pt-16 text-center">
          <div className="inline-flex items-center gap-2 text-secondary-800 text-xs uppercase tracking-widest">
            <Check className="h-4 w-4" /> Received
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-primary-900 mt-3">Thank you.</h1>
          <p className="text-primary-700 mt-3">Your reflection means the world to how we craft the next gathering.</p>
          {data.event?.slug ? (
            <p className="text-neutral-500 text-xs mt-6" data-testid="survey-missed-connection-cta">
              Missed someone at this event? <a
                href={`/introductions/request/${data.event.slug}`}
                className="text-secondary-800 underline hover:text-secondary-900"
                data-testid="survey-missed-connection-link"
              >Send a quiet note back through us.</a>
            </p>
          ) : null}
        </div>
        {recap && !data.is_attendee ? (
          <div className="max-w-3xl mx-auto p-6" data-testid="survey-recap">
            <h2 className="font-display text-xl font-semibold text-primary-900 mb-3">Here is what you missed</h2>
            <div className="grid gap-3 md:grid-cols-2">
              {(recap.recap_photos || []).slice(0, 6).map((p, i) => (
                <img key={i} src={p.url || p} alt={`Event moment ${i + 1}`} className="rounded-lg w-full aspect-video object-cover" />
              ))}
            </div>
            {recap.statistics && recap.statistics.length ? (
              <div className="mt-4 grid gap-2 md:grid-cols-3">
                {recap.statistics.map((s, i) => (
                  <div key={i} className="bg-white rounded-lg p-3 border border-neutral-200 text-center">
                    <div className="text-2xl font-display font-bold text-primary-900">{s.value}</div>
                    <div className="text-xs text-neutral-600 mt-1">{s.label}</div>
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    );
  }

  const isNonAttendee = !data.is_attendee;
  const isLast = step >= stepsMemo.max;
  const current = stepsMemo.steps[step - 1] || { questions: [] };
  const initials = data.attendee_display?.initials || '';
  const firstName = data.attendee_display?.first_name || '';
  // Bundle 180 - full_name attribution needs last name too. Server
  // exposes it under attendee_display when available; falls back
  // to empty string so the JSX join stays safe.
  const lastName = data.attendee_display?.last_name || '';

  return (
    <div className="min-h-screen bg-neutral-50" data-testid="survey-page">
      <div className="max-w-2xl mx-auto p-4 sm:p-6">
        {data.is_preview ? (
          <div
            className="mb-4 rounded-md border border-amber-300
                              bg-amber-50 px-3 py-2 text-xs
                              text-amber-900"
            data-testid="survey-bundle-117-preview-banner"
          >
            <span className="font-semibold uppercase tracking-widest
                                    text-[10px]">
              Preview mode
            </span>
            <span className="ml-2">
              You are viewing an admin test send. Your answers will
              not be saved to any real recipient&apos;s record.
            </span>
          </div>
        ) : null}
        {previewOnly ? (
          <div className="mb-4 rounded-md border border-secondary-300 bg-secondary-50 px-3 py-2 text-xs text-secondary-900" data-testid="survey-preview-only-banner">
            <span className="font-semibold uppercase tracking-widest text-[10px]">Preview only</span>
            <span className="ml-2">Nothing on this page persists. Autosave and submit are disabled.</span>
          </div>
        ) : null}

        {isNonAttendee && !correctionActive ? (
          <div className="mb-4 border border-secondary-200 bg-secondary-50/70 rounded-lg p-3" data-testid="attendance-correction-toggle">
            <div className="flex items-start justify-between gap-3">
              <div className="text-sm text-primary-900">
                <span className="font-semibold">I actually attended this event.</span>
                <div className="text-xs text-primary-700 mt-1">
                  If our check-in record is incorrect, turn this on and we will show you the full attendee survey.
                </div>
              </div>
              <Button size="sm" onClick={toggleAttendanceCorrection}
                className="bg-primary-900 text-white hover:bg-primary-800 shrink-0"
                data-testid="attendance-correction-btn">Turn on</Button>
            </div>
          </div>
        ) : null}
        {isNonAttendee && correctionActive ? (
          <div className="mb-4 border border-emerald-200 bg-emerald-50 rounded-lg p-3 text-sm text-emerald-900" data-testid="attendance-correction-active">
            <ShieldCheck className="h-4 w-4 inline mr-1" /> Correction noted. Rating will start counting once an admin verifies.
          </div>
        ) : null}

        <div className="flex items-center gap-2 text-secondary-800 text-xs uppercase tracking-widest">
          <Sparkles className="h-3.5 w-3.5" /> {data.event?.title}
        </div>
        <h1 className="font-display text-2xl sm:text-3xl font-bold text-primary-900 mt-2">
          {data.is_attendee ? 'Your reflection' : 'A quick note back'}
        </h1>
        <p className="text-primary-700 text-sm mt-2">{data.intro}</p>

        <div className="mt-6 mb-4 flex items-center gap-2" data-testid="survey-progress"
              role="progressbar"
              aria-valuemin={1}
              aria-valuemax={stepsMemo.max}
              aria-valuenow={step}
              aria-label={`Survey progress, step ${step} of ${stepsMemo.max}`}>
          {Array.from({ length: stepsMemo.max }).map((_, i) => (
            <div key={i} className={`h-1.5 flex-1 rounded-full ${i < step ? 'bg-primary-900' : 'bg-neutral-200'}`} />
          ))}
        </div>
        <div className="text-[11px] uppercase tracking-widest text-neutral-500 mb-4">Step {step} of {stepsMemo.max}</div>

        {/* Bundle 102 - aria-live sink used by ChipGroup cap announcer. */}
        <div ref={liveRef} className="sr-only" aria-live="polite"
              data-testid="survey-live-region"></div>

        {autosaveFailed ? (
          <div className="mb-4 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 flex items-center justify-between gap-2"
                data-testid="autosave-retry-banner" role="alert">
            <span>
              We could not save your last change. Your answers are still on this
              page. Please retry, or refresh once your connection is back.
            </span>
            <Button size="sm" onClick={retryAutosave}
              className="bg-amber-700 text-white hover:bg-amber-800 shrink-0"
              data-testid="autosave-retry-btn">Retry</Button>
          </div>
        ) : null}

        {stepErrors.length ? (
          <div
            ref={validationSummaryRef}
            role="alert"
            aria-live="assertive"
            tabIndex={-1}
            className="mb-4 rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-900 focus:outline-none focus:ring-2 focus:ring-red-500"
            data-testid="step-validation-summary"
          >
            <div className="font-semibold mb-1" data-testid="step-validation-summary-title">
              Please complete the following before continuing.
            </div>
            <ul className="list-disc pl-5 space-y-0.5">
              {stepErrors.map((e) => (
                <li key={e.id}>
                  <a
                    href={`#question-${e.id}`}
                    onClick={(evt) => {
                      evt.preventDefault();
                      try {
                        const el = document.querySelector(
                          `[data-testid="question-${e.id}"]`,
                        );
                        if (el) {
                          const focusable = el.querySelector(
                            'input, textarea, select, button, [tabindex]:not([tabindex="-1"])',
                          );
                          (focusable || el).focus();
                          el.scrollIntoView({ block: 'center', behavior: 'smooth' });
                        }
                      } catch (_) { /* noop */ }
                    }}
                    className="underline text-red-800 hover:text-red-900"
                    data-testid={`step-validation-link-${e.id}`}
                  >{e.label}</a>
                  {e.hint ? <span className="text-red-800"> - {e.hint}</span> : null}
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        {(() => {
          const st = (data.step_titles || [])
            .find((t) => t.step === step);
          return st ? (
            <h2
              tabIndex={-1}
              id={`step-${step}-heading`}
              className="font-display text-xl sm:text-2xl font-semibold text-primary-900 mt-2 mb-4"
              data-testid={`step-heading-${step}`}
            >{st.title}</h2>
          ) : null;
        })()}

        <div className="space-y-6">
          {current.questions.map((q) => {
            // Bundle 116 - per-question error surface. aria-invalid on
            // the container, aria-describedby link to the inline
            // error text so screen readers announce the failure.
            const qErr = stepErrors.find((e) => e.id === q.id);
            return (
            <div key={q.id} id={`question-${q.id}`}
                    className="space-y-2"
                    data-testid={`question-${q.id}`}
                    aria-invalid={qErr ? true : undefined}
                    aria-describedby={qErr
                      ? `question-${q.id}-error` : undefined}>
              {q.type !== 'checkbox' ? (
                <label className="text-sm font-medium text-primary-900 leading-snug block"
                        htmlFor={`q-${q.id}`}>
                  {q.label}
                  {q.required ? <span className="text-red-500 ml-1" aria-hidden="true">*</span> : null}
                </label>
              ) : null}
              {/* Bundle 180 - helper_text renders under the label
                  as muted copy. Non-apologetic, gives the guest a
                  reason for the field. Applies to
                  future_attendee_statement and
                  testimonial_permission today. */}
              {q.helper_text && q.type !== 'checkbox' ? (
                <div
                  className="text-[11px] text-neutral-500 -mt-1"
                  data-testid={`question-${q.id}-helper`}
                >
                  {q.helper_text}
                </div>
              ) : null}
              {q.type === 'chips' && q.required
                && (q.min_select || 1) === 1 && q.max_select ? (
                <div className="text-[11px] text-neutral-500"
                        data-testid={`chip-hint-${q.id}`}>
                  Pick between 1 and {q.max_select}.
                </div>
              ) : null}
              <QuestionField q={q} value={answers[q.id]}
                              onChange={(v) => setAnswer(q.id, v)}
                              answers={answers}
                              listErrors={stepErrors.filter(
                                (e) => (e.id || '').startsWith(`${q.id}[`)
                                  || (e.id || '').startsWith(`${q.id}.`),
                              ).map((e) => e.id)}
                              liveRef={liveRef}
              />
              {/* Bundle 180 - render helper under the checkbox
                  because the label sits INSIDE the QuestionField
                  render for checkbox types. */}
              {q.helper_text && q.type === 'checkbox' ? (
                <div
                  className="text-[11px] text-neutral-500 mt-1"
                  data-testid={`question-${q.id}-helper`}
                >
                  {q.helper_text}
                </div>
              ) : null}
              {qErr ? (
                <div id={`question-${q.id}-error`}
                        data-testid={`question-${q.id}-error`}
                        className="text-xs text-red-700 mt-1">
                  {qErr.hint || 'This field is required.'}
                </div>
              ) : null}
              {q.id === 'testimonial_attribution' && answers.testimonial_permission ? (
                <div className="text-[11px] text-neutral-500 mt-1" data-testid="testimonial-attribution-preview">
                  Preview: <span className="font-semibold text-primary-800">
                    {/* Bundle 180 - full_name path added. */}
                    {answers.testimonial_attribution === 'first_name'
                      ? (firstName || 'Guest')
                      : answers.testimonial_attribution === 'full_name'
                        ? ([firstName, lastName].filter(Boolean).join(' ')
                            || firstName || 'Guest')
                        : (initials || 'G')}
                  </span>
                </div>
              ) : null}
            </div>);
          })}
        </div>

        {error ? (
          <div className="mt-4 text-xs text-red-700 bg-red-50 border border-red-200 rounded-md p-2" data-testid="survey-form-error">{error}</div>
        ) : null}

        <div className="flex items-center justify-between mt-8">
          <Button
            variant="ghost"
            onClick={() => goToStep(Math.max(1, step - 1))}
            disabled={step === 1}
            data-testid="survey-back-btn"
            aria-label={step > 1
              ? `Back to step ${step - 1} of ${stepsMemo.max}`
              : 'Back'}
          >
            <ChevronLeft className="h-4 w-4 mr-1" /> Back
          </Button>
          {isLast ? (
            <Button
              onClick={submit}
              disabled={submitting}
              className="bg-primary-900 text-white hover:bg-primary-800"
              data-testid="survey-submit-btn"
              aria-label={`Submit reflection - final step ${step} of ${stepsMemo.max}`}
            >
              {submitting ? 'Submitting...' : 'Submit reflection'}
            </Button>
          ) : (
            <Button
              onClick={() => goToStep(Math.min(stepsMemo.max, step + 1))}
              className="bg-primary-900 text-white hover:bg-primary-800"
              data-testid="survey-next-btn"
              aria-label={`Continue to step ${step + 1} of ${stepsMemo.max}`}
            >
              Next <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          )}
        </div>
        <div className="text-[11px] text-neutral-500 mt-6" data-testid="survey-autosave-note">
          Your answers autosave. You can close this tab and come back to the same link.
        </div>
      </div>
    </div>
  );
};

export default SurveyTokenPage;
